import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { RefreshCwIcon, Trash2Icon } from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import type { AnalysisStatus } from '../../model/types';
import type { AnalysisCardProps } from './AnalysisCard.types';
import { useTranslation } from 'react-i18next';

const STATUS_BADGE_VARIANT = {
  pending: 'warning',
  running: 'warning',
  completed: 'positive',
  failed: 'destructive',
} as const satisfies Record<AnalysisStatus, 'warning' | 'positive' | 'destructive'>;

/**
 * The IQ home card: a compact info card for one analysis.
 */
export function AnalysisCard({
  analysis,
  creator,
  variant = 'default',
  onRetry,
  onDelete,
  isRetrying = false,
  isDeleting = false,
}: AnalysisCardProps) {
  const { t } = useTranslation('analysis');

  const [deleteOpen, setDeleteOpen] = useState(false);

  const isGenerating = analysis.status === 'pending' || analysis.status === 'running';
  const isFailed = analysis.status === 'failed';
  const isCompleted = analysis.status === 'completed';
  const isMutating = isRetrying || isDeleting;
  const showActions = variant === 'default' && (isFailed || isCompleted) && !isMutating;

  const deleteLabel = t('analysis.analysisCardV2.deleteReport');
  const retryLabel = t('analysis.analysisCardV2.retryAnalysis');

  const statusLabel = isGenerating
    ? t('analysis.analysisCardV2.generating')
    : isFailed
      ? t('analysis.analysisCardV2.failed')
      : t('analysis.analysisCardV2.completed');

  const region = analysis.markets?.length ? analysis.markets.join(', ') : null;
  // The list endpoint substitutes the brand for a missing report name, so an
  // unnamed report arrives with the two fields equal rather than a null name.
  const isNamed = analysis.name !== analysis.brandName;
  const title = isNamed ? analysis.name : analysis.brandName;
  const subtitle = isNamed ? [analysis.brandName, region].filter(Boolean).join(' · ') : region;

  const titleText = (
    <TruncatedText value={title} className="text-base font-medium leading-6 text-foreground" />
  );

  const handleConfirmDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onDelete?.(analysis.id, { onSettled: () => setDeleteOpen(false) });
  };

  return (
    <>
      <div
        data-testid={`analysis-card-${analysis.id}`}
        className={clsx(
          'group relative flex w-full flex-col gap-3.5 rounded-lg border border-border-light bg-background p-md',
          isCompleted
            ? 'cursor-pointer transition-colors duration-fast ease-out hover:border-border-strong'
            : 'cursor-default',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Tooltip triggers (truncated text here, the creator avatar below) are raised above
              the link's overlay so they keep receiving the hover. */}
          <div className="flex min-w-0 flex-col [&_[data-state]]:relative [&_[data-state]]:z-10">
            {isCompleted ? (
              // The link's ::after covers the whole card: a click anywhere opens the report, and
              // it behaves as a real link (new tab, copy address). The action buttons stay outside it.
              <Link
                to={ROUTES.IQ_ANALYSIS(analysis.id)}
                aria-label={t('analysis.analysisCardV2.openReport', {
                  title,
                  status: statusLabel,
                })}
                className="min-w-0 outline-none after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:after:focus-ring"
              >
                {titleText}
              </Link>
            ) : (
              titleText
            )}
            {subtitle ? (
              <TruncatedText value={subtitle} className="text-xs leading-4 text-muted-foreground" />
            ) : null}
          </div>
          <Badge
            data-testid="analysis-card-status-badge"
            // A completed card's link already names the status, so the badge would repeat it.
            aria-hidden={isCompleted || undefined}
            variant={STATUS_BADGE_VARIANT[analysis.status]}
            className="gap-1.5 font-normal"
            size="sm"
          >
            {isGenerating ? <Spinner size="sm" /> : null}
            {statusLabel}
          </Badge>
        </div>

        <p className="m-0 line-clamp-2 text-sm leading-5 text-foreground-alt text-pretty">
          {analysis.query}
        </p>

        <div className="mt-auto flex items-center gap-2.5 pt-1 [&_[data-state]]:relative [&_[data-state]]:z-10">
          {creator}
          <span className="flex-1 text-xs leading-4 text-muted-foreground tabular-nums">
            {formatRelativeDate(analysis.createdAt)}
          </span>
          {showActions && (
            // Its own provider rather than the host page's, so the card carries its
            // tooltips wherever it is mounted — as `TruncatedText` above already does.
            <TooltipProvider>
              <div className="relative z-10 flex items-center gap-1 opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 group-focus-within:opacity-100">
                {isFailed && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={retryLabel}
                        disabled={isMutating}
                        onClick={() => onRetry?.(analysis.id)}
                      >
                        <RefreshCwIcon className="size-4 text-foreground-alt" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{retryLabel}</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={deleteLabel}
                      disabled={isMutating}
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash2Icon className="size-4 text-foreground-alt" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{deleteLabel}</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
        </div>
      </div>

      {variant === 'default' && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('analysis.analysisCard.deleteAnalysis')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('analysis.analysisCard.areYouSureYouWantToDelete', {
                  name: analysis.name,
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>
                {t('analysis.analysisCard.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting
                  ? t('analysis.analysisCard.deleting')
                  : t('analysis.analysisCard.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
